/// <reference types="vite/client" />
import { readingProgress, revealTiming } from './lib/motionPolicy';
import './siteMotion.css';

// Shared by every Vite HTML entry, including static guides and late React results.
// No content is hidden while waiting for JavaScript, an observer, or an animation.
export function installSiteMotion() {
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const compact = matchMedia('(max-width: 760px)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const seen = new WeakSet<HTMLElement>();
  const heroes = new WeakSet<HTMLElement>();
  const animations = new Map<HTMLElement, Animation>();
  const cleanup: (() => void)[] = [];
  const pending = new Set<HTMLElement>();
  let scanFrame = 0;
  let progressFrame = 0;
  let reading: HTMLElement | null = null;
  let progress: HTMLElement | null = null;
  let motionPaused = false;
  let stopLottie: (() => void) | undefined;
  let stopped = false;
  let idleHandle: number | undefined;
  // Load artwork after critical page resources, not in the homepage entry bundle.
  const startArtwork = () => {
    const run = () => {
      if (stopped) return;
      void import('./siteLottie').then(module => { if (!stopped) stopLottie = module.installSiteLottie(); }).catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') idleHandle = window.requestIdleCallback(run, { timeout: 2000 });
    else idleHandle = (window as Window).setTimeout(run, 800);
  };
  try { motionPaused = localStorage.getItem('ziheng:motion-paused') === 'true'; } catch { /* Storage can be unavailable in private mode. */ }
  const isReduced = () => reduced.matches || motionPaused;

  const heroSelector = '.mvp-hero-copy, .bazi-landing-hero > div:not(.zm-hero-atmosphere):not(.bazi-chart-teaser), .bazi-hero-copy, .ed-hero, .mvp-guide-hero, .mvp-article > header, .adj-hero';
  const cardSelector = '.journey-doors > a, .mvp-topic-grid > a, .mvp-guide-index > article, .mvp-product-card, .mvp-price-grid > article, .mvp-demo-entry, .ed-card, .bazi-three > article, .bazi-chart-teaser > div, .bazi-pillars > article, .adj-grid > a, .adj-grid > article, .arcana-grid > a, .serp-card-directory > a, .tarot-card-link';
  const revealSelector = `${cardSelector}, .journey-start > header, .mvp-section-copy, .mvp-home-evidence, .mvp-method-list > article, .mvp-result-heading, .mvp-complete-chart > header, .mvp-product-ladder > header, .mvp-reading-card, .mvp-paid-report-grid > article, .mvp-demo-chapter > header, .bazi-result-head, .bazi-analysis-grid > article, .bazi-useful > header, .bazi-timing > header, .bazi-paid > header, .ed-copy > h2, .ed-copy > figure, .mvp-article > h2, .mvp-article > figure, .mvp-article-cta, .ed-cta, .adj-copy > h2`;

  const play = (element: HTMLElement, index: number, entrance = false) => {
    seen.add(element);
    observer?.unobserve(element);
    if (isReduced() || document.hidden || !element.animate || !element.getClientRects().length) return;
    // Don't move the focused element or restart motion as someone interacts.
    if (element.contains(document.activeElement)) return;
    const timing = revealTiming(index, compact.matches);
    animations.get(element)?.cancel();
    const animation = element.animate([
      { opacity: entrance ? .7 : .45, translate: `0 ${timing.distance}px` },
      { opacity: 1, translate: '0 0' },
    ], { duration: timing.duration, delay: timing.delay, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' });
    animations.set(element, animation);
    void animation.finished.then(() => {
      if (animations.get(element) === animation) animations.delete(element);
    }).catch(() => {});
    element.dataset.zmRevealed = 'true';
  };

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;
      const element = entry.target;
      if (seen.has(element)) { observer?.unobserve(element); continue; }
      play(element, Number(element.dataset.zmOrder || 0));
    }
  }, { threshold: 0, rootMargin: '0px 0px -24px 0px' }) : null;

  const atmosphereObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const entry of entries) entry.target.classList.toggle('zm-in-view', entry.isIntersecting);
  }) : null;

  const decorateHero = (hero: HTMLElement) => {
    if (heroes.has(hero)) return;
    heroes.add(hero);
    const atmosphere = document.createElement('div');
    atmosphere.className = 'zm-hero-atmosphere';
    atmosphere.setAttribute('aria-hidden', 'true');
    for (const name of ['light', 'seal', 'thread']) {
      const layer = document.createElement('span');
      layer.className = `zm-hero-${name}`;
      atmosphere.append(layer);
    }
    hero.prepend(atmosphere);
    atmosphereObserver?.observe(atmosphere);
    if (!atmosphereObserver) atmosphere.classList.add('zm-in-view');
    let frame = 0;
    const move = (event: PointerEvent) => {
      if (isReduced() || compact.matches || !fine.matches || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const rect = hero.getBoundingClientRect();
        atmosphere.style.setProperty('--zm-x', `${((event.clientX - rect.left) / rect.width - .5) * 16}px`);
        atmosphere.style.setProperty('--zm-y', `${((event.clientY - rect.top) / rect.height - .5) * 12}px`);
      });
    };
    const leave = () => {
      cancelAnimationFrame(frame); frame = 0;
      atmosphere.style.setProperty('--zm-x', '0px');
      atmosphere.style.setProperty('--zm-y', '0px');
    };
    hero.addEventListener('pointermove', move, { passive: true });
    hero.addEventListener('pointerleave', leave);
    cleanup.push(() => { leave(); hero.removeEventListener('pointermove', move); hero.removeEventListener('pointerleave', leave); atmosphere.remove(); });
  };

  const each = (scope: HTMLElement, selector: string, visit: (element: HTMLElement) => void) => {
    if (scope.matches(selector)) visit(scope);
    scope.querySelectorAll<HTMLElement>(selector).forEach(visit);
  };

  const scan = (scope: HTMLElement) => {
    if (!scope.isConnected) return;
    attachControl(scope);
    attachReading(scope);
    each(scope, cardSelector, (element) => { element.dataset.zmCard = 'true'; });
    each(scope, '.mvp-landing .mvp-hero, .bazi-landing-hero', decorateHero);
    each(scope, heroSelector, (hero) => {
      Array.from(hero.children).filter((child): child is HTMLElement => child instanceof HTMLElement && !child.classList.contains('zm-hero-atmosphere'))
        .forEach((child, index) => {
          if (seen.has(child)) return;
          if (hero.getBoundingClientRect().top < innerHeight) play(child, index, true);
        });
    });
    each(scope, revealSelector, (element) => {
      if (element.dataset.zmReveal) return;
      element.dataset.zmReveal = 'true';
      element.dataset.zmOrder = String(Array.from(element.parentElement?.children || []).indexOf(element) % 4);
      const bounds = element.getBoundingClientRect();
      // First-paint content, restored scroll positions, and hash targets stay visible.
      if (isReduced() || !observer || seen.has(element) || bounds.top < innerHeight) {
        seen.add(element);
        element.dataset.zmRevealed = 'true';
      } else observer.observe(element);
    });
  };

  const updateProgress = () => {
    progressFrame = 0;
    if (!reading?.isConnected || !progress) return;
    const bounds = reading.getBoundingClientRect();
    progress.style.setProperty('--zm-progress', String(readingProgress(bounds.top, bounds.height, innerHeight)));
    progress.dataset.active = String(bounds.top < 0 && bounds.bottom > 0);
  };
  const requestProgress = () => { if (!progressFrame) progressFrame = requestAnimationFrame(updateProgress); };

  let control: HTMLButtonElement | null = null;
  const syncPreference = () => {
    root.classList.toggle('zm-reduced-motion', isReduced());
    if (control) {
      const lang = document.documentElement.lang;
      const zh = lang.startsWith('zh');
      const traditional = lang.toLowerCase().includes('hant');
      control.textContent = reduced.matches
        ? (zh ? (traditional ? '已依系統減少動態效果' : '已依系统减少动态效果') : 'Reduced motion · system setting')
        : isReduced()
          ? (zh ? (traditional ? '開啟動效' : '开启动效') : 'Resume motion')
          : (zh ? (traditional ? '暫停動效' : '暂停动效') : 'Pause motion');
      control.setAttribute('aria-pressed', String(isReduced()));
      control.disabled = reduced.matches;
    }
    if (isReduced()) {
      animations.forEach((animation) => animation.cancel());
      animations.clear();
    }
  };
  const syncVisibility = () => {
    root.classList.toggle('zm-page-hidden', document.hidden);
    if (document.hidden) {
      animations.forEach((animation) => animation.cancel());
      animations.clear();
    }
  };
  const syncStoredPreference = (event: StorageEvent) => {
    if (event.key !== 'ziheng:motion-paused') return;
    motionPaused = event.newValue === 'true';
    syncPreference();
  };

  const attachControl = (scope: HTMLElement) => {
    if (control) return;
    const selector = '.mvp-footer, .ed-footer, .adj-footer, .bazi-footer, body > footer, main > footer:last-child';
    const footer = scope.matches(selector) ? scope : scope.querySelector(selector);
    // Tools mounted by React may have no site footer. Keep the control outside
    // their managed root so a new chart cannot remove or duplicate it.
    const host = footer || document.createElement('div');
    if (!footer) { host.className = 'zm-motion-footer'; document.body.append(host); cleanup.push(() => host.remove()); }
    control = document.createElement('button');
    control.type = 'button'; control.className = 'zm-motion-control';
    control.addEventListener('click', () => {
      motionPaused = !motionPaused;
      try { localStorage.setItem('ziheng:motion-paused', String(motionPaused)); } catch { /* Nonessential preference. */ }
      syncPreference();
    });
    host.append(control);
    syncPreference();
  };
  const attachReading = (scope: HTMLElement) => {
    if (reading) return;
    const selector = '.mvp-article, .ed-copy, .adj-copy, .mvp-demo-report, .mvp-paid-report';
    reading = scope.matches(selector) ? scope : scope.querySelector<HTMLElement>(selector);
    if (!reading) return;
    progress = document.createElement('div');
    progress.className = 'zm-reading-progress';
    progress.setAttribute('aria-hidden', 'true');
    document.body.append(progress);
    window.addEventListener('scroll', requestProgress, { passive: true });
    window.addEventListener('resize', requestProgress, { passive: true });
    updateProgress();
  };

  root.classList.add('zm-motion-ready');
  syncPreference(); syncVisibility(); scan(document.body);

  const mutations = new MutationObserver((records) => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node instanceof HTMLElement && !node.closest('.zl-motion') && !node.classList.contains('zm-hero-atmosphere') && !node.classList.contains('zm-reading-progress')) pending.add(node);
    }
    if (pending.size && !scanFrame) scanFrame = requestAnimationFrame(() => {
      scanFrame = 0;
      pending.forEach(scan); pending.clear(); requestProgress();
    });
  });
  mutations.observe(document.body, { childList: true, subtree: true });
  reduced.addEventListener('change', syncPreference);
  window.addEventListener('storage', syncStoredPreference);
  document.addEventListener('visibilitychange', syncVisibility);
  if (document.readyState === 'complete') startArtwork();
  else window.addEventListener('load', startArtwork, { once: true });

  return () => {
    stopped = true; stopLottie?.(); window.removeEventListener('load', startArtwork);
    if (idleHandle !== undefined) {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle);
      else (window as Window).clearTimeout(idleHandle);
    }
    mutations.disconnect(); observer?.disconnect(); atmosphereObserver?.disconnect();
    cancelAnimationFrame(scanFrame); cancelAnimationFrame(progressFrame);
    animations.forEach((animation) => animation.cancel());
    cleanup.forEach((dispose) => dispose()); progress?.remove(); control?.remove();
    window.removeEventListener('scroll', requestProgress); window.removeEventListener('resize', requestProgress);
    reduced.removeEventListener('change', syncPreference); document.removeEventListener('visibilitychange', syncVisibility);
    window.removeEventListener('storage', syncStoredPreference);
    root.classList.remove('zm-motion-ready', 'zm-reduced-motion', 'zm-page-hidden');
  };
}

let dispose: (() => void) | undefined;
const start = () => { dispose = installSiteMotion(); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
if (import.meta.hot) import.meta.hot.dispose(() => { document.removeEventListener('DOMContentLoaded', start); dispose?.(); });
